import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isIos, isMobileDevice } from 'utils/Helpers';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { useDispatch } from 'react-redux';
import { trackEvent } from 'user-analytics';
import { WALLET_ADD_BUTTON_PRESSED } from 'constants/TrackEvents';
import { downloadPkpass } from 'utils/ticket-wallet';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { isNativeAppWebView } from 'utils/webview';
import useReactNativeMessage from 'hooks/useReactNativeMessage';
import { resolveTicketSeatZone, resolveTicketSeatNumberForDisplay } from 'utils/seatPricing';
import { hasLapChildData } from 'utils/lapChild';
import adjacentLogo from 'images/seats/adjacentLogo.svg';
import PassengerInfoHeader from '../../components/PassengerInfoHeader';
import errorMonitoring from '../../errorMonitoring';
import { setError } from '../../actions';
import WalletButton from '../atoms/WalletButton';

/**
 * TicketPassengerItem component.
 * Pure view component that renders a passenger card the ticket information.
 * @param {object} props - Component props.
 * @param {string} props.name - Passenger full name.
 * @param {string} props.type - Passenger type (e.g., 'Adulto').
 * @param {string|number} props.seat - Seat number.
 * @param {number} props.counter - Passenger counter number for display.
 * @param {boolean} props.isAdjacentSeat - Whether the seat is adjacent to the previous seat.
 * @param {boolean} props.isOpen - Whether the ticket is open.
 * @param {boolean} props.isGoogle - Whether the wallet button should show Google Wallet (true) or Apple Wallet (false).
 * @param {boolean} props.supportsAddToWallet - Whether wallet integration is supported.
 * @param {boolean} props.isPkpassLoading - Whether the wallet download is in progress.
 * @param {Function} props.onAddToWallet - Function to handle adding ticket to wallet.
 * @param {number} [props.departureSeatZone] - Elements NewSeat zone index (1-5).
 * @returns {JSX.Element} Passenger card with wallet button.
 */
export const TicketPassengerItem = ({
  name,
  type,
  seat,
  counter,
  isAdjacentSeat,
  isOpen,
  isGoogle,
  supportsAddToWallet,
  isPkpassLoading,
  onAddToWallet,
  departureSeatZone,
}) => {
  return (
    <div className="p-2 bg-white rounded-3xl w-full border border-gray-200 flex flex-col gap-2">
      <div className="flex flex-row bg-primary-regular/5 p-4 rounded-2xl">
        <div className="flex flex-col justify-between w-full">
          <PassengerInfoHeader
            name={name}
            typePassenger={type}
            isAdjacentSeat={isAdjacentSeat}
            numberSeat={!isOpen && seat}
            numberPassenger={counter}
            departureSeatZone={departureSeatZone}
          />
        </div>
        <span className="font-bold select-none text-primary-regular/10 text-6xl lg:text-3xl content-center">
          {isAdjacentSeat ? (
            <img src={adjacentLogo} alt="Placeholder" className="w-20 h-4" />
          ) : (
            <em>P{counter}</em>
          )}
        </span>
      </div>
      {supportsAddToWallet && (
        <WalletButton
          onClick={onAddToWallet}
          isLoading={isPkpassLoading}
          disabled={isPkpassLoading}
          isGoogle={isGoogle}
        />
      )}
    </div>
  );
};

TicketPassengerItem.propTypes = {
  /** Passenger full name */
  name: PropTypes.string.isRequired,
  /** Passenger type (e.g., 'Adulto') */
  type: PropTypes.string.isRequired,
  /** Seat number */
  seat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Passenger counter number for display */
  counter: PropTypes.number.isRequired,
  /** Whether the seat is adjacent to the previous seat */
  isAdjacentSeat: PropTypes.bool,
  /** Whether the ticket is open */
  isOpen: PropTypes.bool,
  /** Whether the wallet button should show Google Wallet (true) or Apple Wallet (false) */
  isGoogle: PropTypes.bool.isRequired,
  /** Whether wallet integration is supported */
  supportsAddToWallet: PropTypes.bool.isRequired,
  /** Whether the wallet download is in progress */
  isPkpassLoading: PropTypes.bool.isRequired,
  /** Function to handle adding ticket to wallet */
  onAddToWallet: PropTypes.func.isRequired,
  /** Elements NewSeat zone index (1-5) */
  departureSeatZone: PropTypes.number,
};

/**
 * LapChildPassengerItem component.
 * Renders a lap-child sub-row beneath the responsible adult on PurchaseComplete.
 * No seat, no wallet button — lap children share the adult's seat and don't
 * have their own ticket entity.
 * @param {object} props - Component props.
 * @param {string} props.name - Lap-child full name.
 * @returns {JSX.Element} Lap-child sub-row.
 */
export const LapChildPassengerItem = ({ name }) => (
  <div className="p-2 bg-white rounded-3xl w-full border border-gray-200 flex flex-col gap-2">
    <div className="flex flex-row bg-primary-regular/5 p-4 rounded-2xl">
      <div className="flex flex-col justify-between w-full">
        <PassengerInfoHeader name={name} typePassenger="child_in_arms" hideSeat />
      </div>
    </div>
  </div>
);

LapChildPassengerItem.propTypes = {
  /** Lap-child full name */
  name: PropTypes.string.isRequired,
};

/**
 * PassengerTicketsList component.
 * Handles the logic for displaying passenger tickets and managing wallet integration.
 * @param {object} props - Component props.
 * @param {Array} props.tickets - The tickets to display.
 * @param {string} props.purchaseToken - The purchase token for wallet downloads.
 * @param {boolean} props.areOpenTickets - Whether the tickets are open.
 * @param {boolean} [props.applySeatZones] - Whether seat zones should be applied.
 * @param {Array<object>} [props.seatPriceZones] - Seat zones for the current leg.
 * @param {Array<object>} [props.selectedSeats] - Selected seats fallback to resolve zone color.
 * @returns {JSX.Element} List of passenger ticket items.
 */
function PassengerTicketsList({
  tickets,
  passengers,
  purchaseToken,
  areOpenTickets,
  applySeatZones,
  seatPriceZones,
  selectedSeats,
}) {
  const { SUPPORT_TICKET_PKPASS } = useWhitelabelFeatures();
  const env = useWhitelabelEnvs();
  const dispatch = useDispatch();
  const RNMessage = useReactNativeMessage();
  const [pkpassLoading, setPkpassLoading] = useState(null);

  const isiOS = isIos();
  const supportsAddToWallet = SUPPORT_TICKET_PKPASS && isMobileDevice();

  /**
   * Add a ticket to the wallet
   */
  const addToWallet = async (ticketId) => {
    const platform = isiOS ? 'ios' : 'android';
    const isNative = isNativeAppWebView();

    trackEvent(WALLET_ADD_BUTTON_PRESSED, {
      'Download Source': isNative ? 'webview' : 'funnel',
      'Platform': platform,
      ticketId,
      'Purchase Token': purchaseToken,
    });

    if (isNative) {
      RNMessage.post({ type: 'WALLET_PASS_DOWNLOAD', token: purchaseToken, ticketId, platform });
      return;
    }

    try {
      setPkpassLoading(ticketId);
      const apiKey = env.api?.mobileKey;
      await downloadPkpass(purchaseToken, ticketId, apiKey, env.api?.purchaseUrl, platform);
    } catch (error) {
      errorMonitoring.notify({
        error,
        info: {
          from: 'PassengerTicketsList.addToWallet',
          ticketId,
          purchaseToken,
          platform,
          downloadSource: 'funnel',
        },
      });
      dispatch(setError(400, 'ticket_wallet_download_failed', 'error', false));
    } finally {
      setPkpassLoading(null);
    }
  };

  let ticketsCount = 1;
  const ticketsWithCount = tickets.map((ticket) => {
    const ticketWithCounter = {
      ...ticket,
      counter: ticketsCount,
    };

    if (!ticket.isAdjacentSeat) {
      ticketsCount += 1;
    }

    return ticketWithCounter;
  });

  return ticketsWithCount.map((ticket, ticketIndex) => {
    const resolvedZone = applySeatZones
      ? resolveTicketSeatZone(ticket, seatPriceZones, selectedSeats)
      : undefined;
    const resolvedSeat = resolveTicketSeatNumberForDisplay(ticket, selectedSeats, ticketIndex);
    const matchedPassenger = passengers?.find((passenger) => passenger.id === ticket.passengerId);
    const lapChild = matchedPassenger?.meta?.lapChild;
    const hasLapChild = hasLapChildData(lapChild);
    const lapChildFirstName = hasLapChild ? lapChild.firstName || '' : '';
    return (
      <React.Fragment key={ticket.id}>
        <TicketPassengerItem
          name={ticket.firstName}
          type={ticket.category}
          isAdjacentSeat={ticket.isAdjacentSeat}
          seat={resolvedSeat}
          counter={ticket.counter}
          departureSeatZone={resolvedZone}
          isOpen={areOpenTickets}
          supportsAddToWallet={supportsAddToWallet}
          isPkpassLoading={pkpassLoading === ticket.id}
          onAddToWallet={() => addToWallet(ticket.id)}
          isGoogle={!isiOS}
        />
        {hasLapChild && <LapChildPassengerItem name={lapChildFirstName} />}
      </React.Fragment>
    );
  });
}

PassengerTicketsList.propTypes = {
  /** The tickets to display */
  tickets: PropTypes.arrayOf(
    PropTypes.shape({
      /** Ticket unique identifier */
      id: PropTypes.string.isRequired,
      /** Passenger first name */
      firstName: PropTypes.string.isRequired,
      /** Passenger category/type */
      category: PropTypes.string.isRequired,
      /** Whether the seat is adjacent to the previous seat */
      isAdjacentSeat: PropTypes.bool.isRequired,
      /** Seat number */
      seat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  /** Purchase passengers (camelized) used to surface lap-child sub-rows */
  passengers: PropTypes.arrayOf(PropTypes.object),
  /** The purchase token for wallet downloads */
  purchaseToken: PropTypes.string.isRequired,
  /** Whether the tickets are open */
  areOpenTickets: PropTypes.bool.isRequired,
  /** Whether seat zones should be applied in complete view */
  applySeatZones: PropTypes.bool,
  /** Seat price zones for current leg */
  seatPriceZones: PropTypes.arrayOf(PropTypes.object),
  /** Selected seats for current leg (used as fallback to resolve zone color in complete view) */
  selectedSeats: PropTypes.arrayOf(PropTypes.object),
};

PassengerTicketsList.defaultProps = {
  passengers: [],
  applySeatZones: false,
  seatPriceZones: [],
  selectedSeats: [],
};

export default PassengerTicketsList;
